import axios from 'axios';

/**
 * AI Consent Service
 *
 * Manages user consent for AI features (OpenAI third-party processing).
 * Implements GDPR/DPDPA compliance with opt-in consent mechanism.
 */
class AIConsentService {
    /**
     * Get current user's AI consent status
     *
     * @returns {Promise<{aiConsentGiven: boolean, aiConsentDate: string|null}>}
     */
    async getStatus() {
        try {
            const response = await axios.get('/api/user/ai-consent/status');
            return response.data;
        } catch (error) {
            console.error('Failed to get AI consent status:', error);
            throw error;
        }
    }

    /**
     * Enable AI features (grant consent)
     *
     * @returns {Promise<{success: boolean, message: string, data: object}>}
     */
    async enable() {
        try {
            const response = await axios.post('/api/user/ai-consent/enable');
            return response.data;
        } catch (error) {
            console.error('Failed to enable AI consent:', error);
            throw error;
        }
    }

    /**
     * Disable AI features (revoke consent)
     *
     * @returns {Promise<{success: boolean, message: string, data: object}>}
     */
    async disable() {
        try {
            const response = await axios.post('/api/user/ai-consent/disable');
            return response.data;
        } catch (error) {
            console.error('Failed to disable AI consent:', error);
            throw error;
        }
    }

    /**
     * Check if error is due to missing AI consent
     *
     * @param {Error} error - Axios error object
     * @returns {boolean} True if error is AI_CONSENT_REQUIRED
     */
    isConsentRequired(error) {
        return (
            error.response &&
            error.response.status === 403 &&
            error.response.data &&
            error.response.data.error === 'AI_CONSENT_REQUIRED'
        );
    }
}

export const aiConsentService = new AIConsentService();
